/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/mode-page.tsx` (939 LOC server component).
 *
 * Changes:
 *   resolveTenant / listCustomDomains / getLaneRow / loadModeData / getDb /
 *   loadPropertyConfigs / pulse*Enabled → `useApi(modes.shell)` + `useApi(modes.data)`
 *   next/redirect → useNavigate
 *   next/link → RouterLink (via child components)
 *   buildPulseSpine act data → omitted on first land (PulseSpine wall/read still
 *     load client-side via existing pulse contracts; act is mode-root-only)
 *   buildSectionTitleToolbar / buildIssuesDetailHeader → local client ports
 *   LanePage / DOMAIN_MOUNTS / ModePlay / ObjectsTable → local ports
 *
 * Live Next original under excellent/app/_modes/mode-page.tsx is UNTOUCHED (D6).
 */

import type { ReactNode } from "react";
import { useEffect } from "react";
import {
  ModeShell,
  StatStrip,
  EmptyState,
  PulseSpine,
  PulseGutterRail,
  SectionTitleBar,
  SectionDashboard,
  type CommitmentTableRow,
  type ModeShellView,
  type NavTab,
  type SectionDashboardEntry,
  type SpineAltitude,
  type StatStripItem,
} from "@excellent/ui";
import { LayoutDashboard, Play, Boxes } from "@excellent/ui/icons";
import { useNavigate } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { ObjectsTable } from "./objects-table";
import { ModePlay } from "./mode-play";
import {
  getMode,
  modePath,
  type ModeSlug,
  type ModeView,
} from "@/modes/modes-registry";
import {
  getDomain,
  getDomainView,
  domainsForMode,
  domainHomeRoute,
  domainHomeView,
  domainNavHref,
  domainSectionPath,
  buildDomainViewProps,
  areaToPath,
  visibleDomainViews,
  type Domain,
  type CustomDomainInput,
} from "@/modes/domains";
import {
  DOMAIN_MOUNTS,
  domainIcon,
  mountSelfPads,
  MOUNTS_WITH_OWN_VIEW_NAV,
} from "./domain-mounts";
import { LanePage } from "./lane-page";
import { buildSectionTitleToolbar } from "./section-title-toolbars";
import { IssuesDetailHeader } from "./issues-detail-header";

const NESTED: Record<string, { label: string; kind: string }> = {
  finance: { label: "Finance", kind: "transaction" },
};

function modeSubNavViews(
  mode: ModeSlug,
  activeSlug: string | undefined,
  projectSlug: string | null | undefined,
  customDomains: CustomDomainInput[],
): ModeShellView[] {
  return domainsForMode(mode, customDomains)
    .filter((d) => !d.hidden)
    .map((d) => ({
      href: domainNavHref(d, projectSlug ?? null),
      label: d.label,
      icon: domainIcon(d.icon),
      active: activeSlug === d.slug,
    }));
}

function sectionEntries(mode: ModeSlug, domain: Domain): SectionDashboardEntry[] {
  return visibleDomainViews(domain)
    .filter((v) => v.component || v.route)
    .map((v) => ({
      key: v.slug,
      label: v.label,
      href: v.route ?? areaToPath(domain.mode, domain.slug, v.slug),
      hint: v.label,
    }));
}

function countPhases(rows: { state: string }[]): Record<string, number> {
  const c: Record<string, number> = {
    proposed: 0,
    active: 0,
    review: 0,
    fulfilled: 0,
    cancelled: 0,
  };
  for (const r of rows) {
    const k = (r.state ?? "").toLowerCase();
    if (k in c) c[k] += 1;
  }
  return c;
}

function DashboardView({
  counts,
  total,
  question,
}: {
  counts: Record<string, number>;
  total: number;
  question: string;
}) {
  const kpis: StatStripItem[] = [
    { title: "Open", value: String((counts.proposed ?? 0) + (counts.active ?? 0)) },
    { title: "In review", value: String(counts.review ?? 0) },
    { title: "Fulfilled", value: String(counts.fulfilled ?? 0) },
    { title: "Total", value: String(total) },
  ];
  return (
    <div className="flex flex-col gap-4">
      <p className="type-body text-muted-foreground">{question}</p>
      <StatStrip items={kpis} variant="standalone" />
      {total === 0 ? (
        <EmptyState
          variant="inline"
          icon={<LayoutDashboard className="size-5" />}
          title="What matters here"
          description="KPIs, the attention list, health, and recent activity for this mode appear as work flows in."
        />
      ) : null}
    </div>
  );
}

export function ModePage({
  mode,
  view = "dashboard",
  area,
  sub,
  searchParams,
  projectSlug = null,
  dashboardSlot,
  titleToolbar,
  titleTabs,
  viewsOverride,
  hideHeader,
}: {
  mode: ModeSlug;
  view?: ModeView;
  area?: string;
  sub?: string;
  searchParams?: Record<string, string | string[] | undefined>;
  projectSlug?: string | null;
  dashboardSlot?: ReactNode;
  /** Mode-root title-row actions (Work's period filter + create "+", …). */
  titleToolbar?: ReactNode;
  /**
   * Mode-root view sub-nav graduated into the masthead (e.g. Customers'
   * List·Pipeline·Forecast·Queue cuts). Threaded to SectionTitleBar.tabs.
   * Restored for RO-3-04 — present on the Next ModePage, dropped from the
   * RO-3-00c port until a section needed it.
   */
  titleTabs?: NavTab[];
  viewsOverride?: ModeShellView[];
  hideHeader?: boolean;
}) {
  const navigate = useNavigate();
  const def = getMode(mode);

  const { data: shell, isLoading: shellLoading } = useApi(modes.shell, {
    query: {
      mode,
      ...(area ? { area } : {}),
      ...(projectSlug ? { projectSlug } : {}),
    },
  });

  // Lazy: only fetch commitment index when a branch needs rows.
  const needsData =
    view === "objects" ||
    view === "dashboard" ||
    Boolean(area) ||
    (mode === "operations" && Boolean(area && NESTED[area]));
  const { data: modeData } = useApi(
    modes.data,
    needsData ? { query: { mode } } : null,
  );

  // Redirects (client). Seam navigate is Next-shaped {push,replace,…}, not TanStack callable.
  useEffect(() => {
    if (mode === "agents" && view === "analytics" && !projectSlug) {
      navigate.replace("/");
      return;
    }
    if (mode === "customers" && projectSlug && !area) {
      navigate.replace("/customers");
    }
    if (mode === "product" && area === "projects" && !projectSlug) {
      navigate.replace("/projects");
    }
    if (view === "analytics" && !(mode === "operations" && area && NESTED[area])) {
      navigate.replace(modePath(mode, { projectSlug }));
    }
    if (area) {
      const d = getDomain(mode, area);
      if (d) {
        const current = sub ? getDomainView(d, sub) : domainHomeView(d);
        if (current?.hidden) {
          const home = domainHomeView(d);
          if (home.slug !== current.slug) {
            navigate.replace(areaToPath(d.mode, d.slug, home.slug, projectSlug));
          }
        }
      }
    }
  }, [mode, view, area, sub, projectSlug, navigate]);

  if (!def) return null;
  if (shellLoading || !shell) return null;

  const workspaceId = shell.workspaceId;
  const customDomains = shell.customDomains as CustomDomainInput[];
  const pulseOn = mode !== "agents" && shell.pulse.dashboard;
  const selfNav = shell.pulse.selfNav;
  const pulseDynamic = shell.pulse.dynamic;

  const spineAltitude: SpineAltitude = !pulseOn
    ? "off"
    : area
      ? "strip"
      : view === "dashboard"
        ? dashboardSlot
          ? "strip"
          : "aggregate-mode"
        : "strip";

  const sectionBack = { label: def.label, href: modePath(mode, { projectSlug }) };

  const pulseFor = (
    leaf: string,
    opts?: {
      compact?: boolean;
      titleToolbar?: ReactNode;
      description?: ReactNode;
      back?: { label: string; href?: string };
      meta?: Parameters<typeof SectionTitleBar>[0]["meta"];
      tabs?: NavTab[];
      navRight?: ReactNode;
    },
  ): ReactNode => {
    const rail = selfNav ? (
      <PulseGutterRail
        scope={area ? `${mode}:${area}` : mode}
        projectSlug={projectSlug ?? undefined}
        dynamic={pulseDynamic}
      />
    ) : undefined;
    return (
      <div className={`flex flex-col ${opts?.compact ? "gap-4" : "gap-7"}`}>
        <SectionTitleBar
          title={leaf}
          description={opts?.description}
          back={opts?.back}
          meta={opts?.meta}
          tabs={opts?.tabs}
          navRight={opts?.navRight}
          actions={opts?.titleToolbar}
        />
        {spineAltitude === "off" ? rail : null}
        <PulseSpine
          altitude={spineAltitude}
          scope={mode}
          title={leaf}
          compact={opts?.compact}
          projectSlug={projectSlug ?? undefined}
          current={area}
          wall={rail}
        />
      </div>
    );
  };

  const pulseNode: ReactNode = pulseFor(def.label, {
    description: def.description,
    ...(titleToolbar ? { titleToolbar } : {}),
    ...(titleTabs && titleTabs.length > 0 ? { tabs: titleTabs } : {}),
  });

  // ── Domain / Lane branch ──────────────────────────────────────────────────
  if (area) {
    const domain =
      getDomain(mode, area, customDomains) ??
      (shell.lane
        ? ({
            mode,
            slug: shell.lane.slug,
            label: shell.lane.label,
            icon: "boxes",
            objectKinds: shell.lane.object_kinds ?? [],
            views: [{ slug: shell.lane.slug, label: shell.lane.label, kind: "list" as const }],
            homeRoute: `/${mode}?area=${shell.lane.slug}`,
            tier: "T2" as const,
            status: "rebuild" as const,
          } satisfies Domain)
        : null);

    // Route-owning hidden aliases redirect to their target. A hidden
    // component-mounted domain is a nav hide (Phase 2.2): it stays resolvable
    // and renders in place so the surface remains substrate, not a teardown.
    if (domain?.hidden) {
      const aliasRoute = domain.views[0]?.route;
      if (aliasRoute) {
        navigate.replace(aliasRoute);
        return null;
      }
    }

    // Lane path
    if (shell.lane && shell.lane.slug === area) {
      const laneDomain: Domain = {
        mode,
        slug: shell.lane.slug,
        label: shell.lane.label,
        icon: "boxes",
        objectKinds: shell.lane.object_kinds ?? [],
        views: [{ slug: shell.lane.slug, label: shell.lane.label, kind: "list" }],
        homeRoute: `/${mode}?area=${shell.lane.slug}`,
        tier: "T2",
        status: "rebuild",
      };
      const subNav = modeSubNavViews(mode, area, projectSlug, customDomains);
      return (
        <LanePage
          mode={mode}
          lane={laneDomain}
          laneUid={shell.lane.uid}
          laneStatus={shell.lane.status}
          laneDescription={shell.lane.description ?? undefined}
          underlying={null}
          sub={sub}
          projectSlug={projectSlug}
          searchParams={searchParams}
          subNav={subNav}
          workspaceId={workspaceId}
          pulse={pulseFor(shell.lane.label, {
            compact: true,
            description: shell.lane.description,
            back: sectionBack,
          })}
        />
      );
    }

    if (domain) {
      const domainViews = modeSubNavViews(mode, domain.slug, projectSlug, customDomains);
      const dv = sub ? getDomainView(domain, sub) : domainHomeView(domain);
      // Hidden views stay registered for deep links but do not mount — the
      // effect above hops them onto the domain's visible home (Company ATS →
      // Team). Return null here so we don't paint the demoted body first.
      if (dv?.hidden) return null;
      // Domain-flip: a view that owns a `route` (not a mount) leaves this mode.
      if (dv?.route) {
        navigate.replace(dv.route);
        return null;
      }
      const componentKey = dv?.component;
      const Mount = componentKey ? DOMAIN_MOUNTS[mode]?.[componentKey] : undefined;

      // Issues editorial header
      const isIssues =
        componentKey === "work.queue" || componentKey === "work.planner";
      if (isIssues) {
        return (
          <ModeShell
            mode={domain.label}
            modeSlug={mode}
            modeHref={domainHomeRoute(domain)}
            views={domainViews}
            hideHeader
            pulse={
              <IssuesDetailHeader
                searchParams={searchParams}
                workspaceId={workspaceId}
                backHref={sectionBack.href}
                backLabel={sectionBack.label}
              />
            }
            playStrip={
              <ModePlay hideWhenEmpty limit={3} mode={mode} kinds={domain.objectKinds} />
            }
            gutter={!mountSelfPads(componentKey)}
          >
            {Mount ? (
              <Mount
                {...buildDomainViewProps(domain, dv!, projectSlug, sub, searchParams)}
                headerExternal
              />
            ) : null}
          </ModeShell>
        );
      }

      if (Mount && dv) {
        const sectionToolbar = buildSectionTitleToolbar(mode, componentKey, {
          searchParams,
          workspaceId,
          projectSlug: projectSlug ?? undefined,
        });
        const viewTabs: NavTab[] =
          componentKey && MOUNTS_WITH_OWN_VIEW_NAV.has(componentKey)
            ? []
            : visibleDomainViews(domain)
                .filter((v) => v.component && !v.route)
                .map((v) => ({
                  href: areaToPath(domain.mode, domain.slug, v.slug, projectSlug),
                  label: v.label,
                }));
        return (
          <ModeShell
            mode={domain.label}
            modeSlug={mode}
            modeHref={domainHomeRoute(domain)}
            views={domain.slug === "company" ? [] : domainViews}
            gutter={!mountSelfPads(componentKey)}
            pulse={pulseFor(domain.label, {
              compact: true,
              titleToolbar: sectionToolbar,
              description: domain.description,
              back: domain.slug === "company" ? undefined : sectionBack,
              tabs: viewTabs.length > 1 ? viewTabs : undefined,
            })}
            playStrip={
              domain.slug === "company" ? undefined : (
                <ModePlay hideWhenEmpty limit={3} mode={mode} kinds={domain.objectKinds} />
              )
            }
          >
            <Mount {...buildDomainViewProps(domain, dv, projectSlug, sub, searchParams)} />
          </ModeShell>
        );
      }

      // Graceful degrade — SectionDashboard
      const rows = domain.objectKinds.length
        ? (modeData?.rows ?? []).filter((r) => domain.objectKinds.includes(r.kind))
        : (modeData?.rows ?? []);
      return (
        <ModeShell
          mode={domain.label}
          modeSlug={mode}
          modeHref={domainHomeRoute(domain)}
          views={domainViews}
          pulse={pulseFor(domain.label, {
            compact: true,
            description: domain.description,
            back: sectionBack,
          })}
          playStrip={
            <ModePlay hideWhenEmpty limit={3} mode={mode} kinds={domain.objectKinds} />
          }
        >
          <SectionDashboard
            entries={sectionEntries(mode, domain)}
            rows={rows as CommitmentTableRow[]}
            from={mode}
            objectsAside={`Everything in ${domain.label}`}
          />
        </ModeShell>
      );
    }
  }

  // Nested Operations ▸ Finance
  if (mode === "operations" && area && NESTED[area]) {
    const nested = NESTED[area]!;
    const rows = (modeData?.rows ?? []).filter((r) => r.kind === nested.kind);
    const nestHref = (v?: ModeView) =>
      modePath("operations", { projectSlug, view: v, extra: { area } });
    if (view === "analytics") {
      navigate.replace(nestHref());
      return null;
    }
    const nestViews: ModeShellView[] = [
      { href: nestHref(), label: "Dashboard", icon: <LayoutDashboard className="size-4" /> },
      { href: nestHref("queue"), label: "Queue", icon: <Play className="size-4" /> },
      { href: nestHref("objects"), label: "Objects", icon: <Boxes className="size-4" /> },
    ];
    return (
      <ModeShell
        mode={nested.label}
        modeSlug={mode}
        modeHref={nestHref()}
        views={nestViews}
        pulse={pulseFor(nested.label, { compact: true, back: sectionBack })}
        playStrip={
          view === "queue" ? undefined : <ModePlay hideWhenEmpty limit={3} mode={mode} />
        }
      >
        {view === "objects" ? (
          <ObjectsTable rows={rows as CommitmentTableRow[]} from="operations" />
        ) : view === "queue" ? (
          <ModePlay title={`What to do next in ${nested.label}`} limit={8} mode={mode} />
        ) : (
          <DashboardView
            counts={countPhases(rows)}
            total={rows.length}
            question={`how is ${nested.label.toLowerCase()}?`}
          />
        )}
      </ModeShell>
    );
  }

  // Mode home
  const homeDomainNav: ModeShellView[] =
    mode === "agents" ? [] : modeSubNavViews(mode, undefined, projectSlug, customDomains);
  const views: ModeShellView[] =
    viewsOverride ??
    (homeDomainNav.length > 0
      ? homeDomainNav
      : [
          {
            href: modePath(mode, { projectSlug }),
            label: "Dashboard",
            icon: <LayoutDashboard className="size-4" />,
          },
          {
            href: modePath(mode, { projectSlug, view: "queue" }),
            label: "Queue",
            icon: <Play className="size-4" />,
          },
          {
            href: modePath(mode, { projectSlug, view: "objects" }),
            label: "Objects",
            icon: <Boxes className="size-4" />,
          },
        ]);

  return (
    <ModeShell
      mode={def.label}
      modeSlug={mode}
      hideHeader={hideHeader}
      modeHref={modePath(mode, { projectSlug })}
      views={views}
      pulse={pulseNode}
      playStrip={
        view === "queue" ||
        (view === "dashboard" && (spineAltitude !== "off" || dashboardSlot))
          ? undefined
          : <ModePlay hideWhenEmpty limit={3} mode={mode} />
      }
    >
      {view === "dashboard"
        ? (dashboardSlot ??
          (spineAltitude === "off" ? (
            <DashboardView
              counts={modeData?.phaseCounts ?? {}}
              total={modeData?.total ?? 0}
              question={def.question}
            />
          ) : null))
        : null}
      {view === "queue" ? (
        <ModePlay title={`What to do next — ${def.label}`} limit={8} mode={mode} />
      ) : null}
      {view === "objects" ? (
        <ObjectsTable
          rows={(modeData?.rows ?? []) as CommitmentTableRow[]}
          from={mode}
        />
      ) : null}
    </ModeShell>
  );
}
